'use client';

import { useCallback, useState } from 'react';

import { Flag, MoreHorizontal, Send, Trash2, UserRound, X } from 'lucide-react';

import {
  type BasicUserProfileInfo,
  type CommentMessage,
  useClipReplies,
} from '../../functions';
import { ReplyItem } from './ReplyItem';

type CommentItemProps = {
  item: CommentMessage;
  clipId: string;
  currentUserId?: string;
  currentUserMeta?: BasicUserProfileInfo | null;
  onRetryComment: (id: string) => void;
  onDeleteComment: (clipId: string, timestamp: number) => void;
};

export function CommentItem({
  item,
  clipId,
  currentUserId,
  currentUserMeta,
  onRetryComment,
  onDeleteComment,
}: CommentItemProps) {
  const {
    replies,
    addReply,
    fetchMoreReplies,
    isReplyLoading,
    isFetchingMoreReplies,
    hasMoreReplies,
  } = useClipReplies(item.id, clipId, item.timestamp, item.replyCount > 0);

  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplies, setShowReplies] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const onSendReply = useCallback(async () => {
    if (!replyText.trim() || isReplyLoading || !currentUserId) return;
    const text = replyText.trim();
    setReplyText('');
    setShowReplies(true);
    await addReply(text, currentUserId, currentUserMeta ?? null);
  }, [replyText, isReplyLoading, currentUserId, currentUserMeta, addReply]);

  const isOwner = item.userId === currentUserId;

  return (
    <div className="mb-5 flex items-start gap-2.5">
      {/* Avatar */}
      {!item.isDeleted && item.avatar ? (
        <img
          src={item.avatar}
          alt={item.name}
          className="h-10 w-10 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div className="bg-raise/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <UserRound className="text-content-1 h-6 w-6" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        {/* Header */}
        <div className="flex items-center justify-between">
          {!item.isDeleted && (
            <span className="text-content-1 max-w-[200px] truncate text-base font-semibold">
              {item.username ?? item.name}
            </span>
          )}

          {/* Menu */}
          {!item.isDeleted && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu(v => !v)}
                className="text-raise/50 hover:text-content-1 p-1"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="bg-line absolute right-0 top-full z-50 mt-1 min-w-[120px] overflow-hidden rounded-lg shadow-lg">
                    {isOwner && (
                      <button
                        type="button"
                        className="hover:bg-raise/10 flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400"
                        onClick={() => {
                          onDeleteComment(clipId, item.timestamp);
                          setShowMenu(false);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    )}
                    {!isOwner && (
                      <button
                        type="button"
                        className="text-content-1 hover:bg-raise/10 flex w-full items-center gap-2 px-3 py-2 text-sm"
                        onClick={() => setShowMenu(false)}
                      >
                        <Flag className="h-3.5 w-3.5" />
                        Report
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Comment text */}
        {!item.isDeleted ? (
          <p className="text-content-1 mt-1 text-sm">{item.text}</p>
        ) : (
          <p className="text-raise/50 mt-1 text-sm">
            This comment has been deleted.
          </p>
        )}

        {/* Retry button */}
        {item.status === 'failed' && (
          <button
            type="button"
            className="mt-1 text-xs font-semibold text-red-400"
            onClick={() => onRetryComment(item.id)}
          >
            Retry
          </button>
        )}

        {item.status === 'uploading' && (
          <p className="text-raise/40 mt-1 text-[10px]">Sending...</p>
        )}

        {/* Reply actions */}
        {item.status !== 'failed' && !item.isDeleted && (
          <div className="mt-1 flex items-center gap-3">
            {replies.length > 0 && !showReplies && (
              <button
                type="button"
                className="text-raise/50 hover:text-raise/70 text-xs"
                onClick={() => {
                  setShowReplies(true);
                  setShowReplyInput(true);
                }}
              >
                Show Replies
              </button>
            )}
            {!showReplyInput && (
              <button
                type="button"
                className="text-raise/50 hover:text-raise/70 text-xs"
                onClick={() => setShowReplyInput(true)}
              >
                Reply
              </button>
            )}
          </div>
        )}

        {/* Replies */}
        {showReplies &&
          replies.map(reply => <ReplyItem key={reply.id} reply={reply} />)}

        {/* Load more / hide replies */}
        {showReplies && replies.length > 0 && (
          <div className="ml-6 mt-2 flex items-center gap-3">
            {hasMoreReplies && (
              <button
                type="button"
                className="text-raise/50 hover:text-raise/70 text-xs"
                onClick={fetchMoreReplies}
                disabled={isFetchingMoreReplies}
              >
                {isFetchingMoreReplies ? (
                  <span className="animate-pulse">Loading...</span>
                ) : (
                  'Load More Replies'
                )}
              </button>
            )}
            <button
              type="button"
              className="text-raise/50 hover:text-raise/70 text-xs"
              onClick={() => {
                setShowReplies(false);
                setShowReplyInput(false);
              }}
            >
              Hide Replies
            </button>
          </div>
        )}

        {/* Reply input */}
        {!item.isDeleted && showReplyInput && (
          <div className="mt-2 flex items-center gap-2">
            {currentUserMeta?.avatar ? (
              <img
                src={currentUserMeta.avatar}
                alt=""
                className="h-7 w-7 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="bg-raise/20 flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                <UserRound className="text-content-1 h-4 w-4" />
              </div>
            )}
            <div className="flex flex-1 items-center rounded-full border border-white px-3 py-1">
              <input
                type="text"
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void onSendReply();
                }}
                placeholder="Write a reply..."
                className="text-content-1 placeholder:text-raise/40 flex-1 bg-transparent text-sm outline-none"
              />
              <button
                type="button"
                onClick={onSendReply}
                disabled={!replyText.trim() || isReplyLoading}
                className="text-content-1 ml-2 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowReplyInput(false)}
              className="text-raise/40 hover:text-raise/60"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
